import { z } from 'zod';
import {
  AUDIO_ERROR_CODES,
} from '../constants/error-codes';
import {
  AUDIO_ATTACHMENT_MIME_TYPES,
  AUDIO_TRANSCRIPTION_LIMITS,
} from '../constants/audio-transcription';

const conversationIdSchema = z.uuid({
  error: 'L identifiant de conversation est invalide.',
});

const isoDateTimeSchema = z.string().datetime({
  error: 'La date doit être au format ISO 8601.',
});

export const audioAttachmentMimeSchema = z.enum(AUDIO_ATTACHMENT_MIME_TYPES, {
  error: 'Le type MIME audio n’est pas supporté.',
});

export const chatAttachmentKindSchema = z.enum(['audio', 'document'], {
  error: 'Le type de pièce jointe est invalide.',
});

export const audioUploadStatusSchema = z.enum([
  'failed',
  'prepared',
  'ready',
  'uploading',
  'validating',
], {
  error: 'Le statut d’upload audio est invalide.',
});

export const audioUploadStrategySchema = z.enum(['multipart', 'simple_put'], {
  error: 'La stratégie d’upload audio est invalide.',
});

// Audio analysis is self-hosted only (ADR-2026-10-06).
export const audioAnalysisProviderSchema = z.enum(['selfhosted'], {
  error: 'Le fournisseur d’analyse audio est invalide.',
});

export const audioTranscriptionJobStatusSchema = z.enum([
  'cancel_requested',
  'canceled',
  'dead',
  'failed',
  'processing',
  'queued',
  'succeeded',
], {
  error: 'Le statut du job de transcription est invalide.',
});

export const audioTranscriptionPhaseSchema = z.enum([
  'aligning',
  'completed',
  'diarizing',
  'finalizing',
  'preprocessing',
  'probing',
  'queued',
  'segmenting',
  'transcribing',
], {
  error: 'La phase de transcription est invalide.',
});

export const audioTranscriptionBatchStatusSchema = z.enum([
  'analyzing',
  'cancel_requested',
  'canceled',
  'failed',
  'partially_succeeded',
  'processing',
  'queued',
  'succeeded',
], {
  error: 'Le statut du lot de transcription est invalide.',
});

export const audioOutputIntentSchema = z.enum([
  'custom_analysis',
  'meeting_minutes',
  'raw_transcript',
  'summary',
], {
  error: 'L’intention de sortie audio est invalide.',
});

export const audioOutputDeliverySchema = z.enum([
  'chat',
  'chat_and_docx',
  'docx',
], {
  error: 'Le mode de livraison audio est invalide.',
});

export const audioOutputToneSchema = z.enum([
  'concise',
  'custom',
  'detailed',
  'executive',
  'neutral',
], {
  error: 'Le ton de sortie audio est invalide.',
});

export const audioArtifactKindSchema = z.enum([
  'combined_docx',
  'file_docx',
  'transcript_json',
  'transcript_text',
], {
  error: 'Le type d’artefact audio est invalide.',
});

export const audioErrorCodeSchema = z.enum(AUDIO_ERROR_CODES, {
  error: 'Le code d’erreur audio est invalide.',
});

export const audioOutputPlanSectionSchema = z.object({
  instruction: z.string().trim().min(1, {
    error: 'L’instruction de section est requise.',
  }).max(2_000, {
    error: 'L’instruction de section est trop longue.',
  }),
  order: z.number().int().min(1).max(40),
  title: z.string().trim().min(1, {
    error: 'Le titre de section est requis.',
  }).max(160, {
    error: 'Le titre de section est trop long.',
  }),
}).strict();

export const audioOutputPlanSchema = z.object({
  customTone: z.string().trim().min(1).max(200).optional(),
  delivery: audioOutputDeliverySchema,
  includeActions: z.boolean(),
  includeDecisions: z.boolean(),
  includeKeyPoints: z.boolean(),
  includeOpenQuestions: z.boolean(),
  intent: audioOutputIntentSchema,
  language: z.union([
    z.literal('auto'),
    z.string().trim().min(2).max(16),
  ]),
  sections: z.array(audioOutputPlanSectionSchema).max(24),
  separateFiles: z.boolean(),
  title: z.string().trim().min(1).max(160).optional(),
  tone: audioOutputToneSchema.optional(),
}).strict().superRefine((plan, context) => {
  if (plan.tone === 'custom' && !plan.customTone) {
    context.addIssue({
      code: 'custom',
      message: 'Un ton personnalisé est requis.',
      path: ['customTone'],
    });
  }

  if (plan.intent === 'raw_transcript' && plan.delivery === 'chat') {
    context.addIssue({
      code: 'custom',
      message: 'Une transcription brute doit produire un DOCX.',
      path: ['delivery'],
    });
  }
});

export const defaultAudioOutputPlan = {
  delivery: 'chat',
  includeActions: true,
  includeDecisions: true,
  includeKeyPoints: true,
  includeOpenQuestions: true,
  intent: 'summary',
  language: 'auto',
  sections: [
    {
      instruction: 'Lister les points clés sans inventer d’information.',
      order: 1,
      title: 'Points clés',
    },
    {
      instruction: 'Lister uniquement les décisions explicitement prononcées.',
      order: 2,
      title: 'Décisions',
    },
    {
      instruction: 'Lister les actions à mener avec le responsable s’il est nommé.',
      order: 3,
      title: 'Actions',
    },
    {
      instruction: 'Lister les questions restées ouvertes.',
      order: 4,
      title: 'Questions ouvertes',
    },
  ],
  separateFiles: false,
  tone: 'neutral',
} as const satisfies z.input<typeof audioOutputPlanSchema>;

export const audioUploadLimitsSchema = z.object({
  maxBatchDurationSeconds: z.number().int().positive(),
  maxDurationSeconds: z.number().int().positive(),
  maxFiles: z.number().int().positive(),
  maxSizeBytes: z.number().int().positive(),
  multipartPartSizeBytes: z.number().int().positive(),
  simplePutMaxBytes: z.number().int().positive(),
}).strict();

export const prepareAudioUploadRequestSchema = z.object({
  conversationId: conversationIdSchema.optional(),
  filename: z.string().trim().min(1, {
    error: 'Le nom du fichier audio est requis.',
  }).max(255, {
    error: 'Le nom du fichier audio est trop long.',
  }),
  mediaType: audioAttachmentMimeSchema,
  sizeBytes: z.number().int().positive({
    error: 'La taille du fichier audio est invalide.',
  }).max(AUDIO_TRANSCRIPTION_LIMITS.maxSizeBytes, {
    error: 'Le fichier audio dépasse la taille maximale autorisée.',
  }),
}).strict();

const audioPresignedPartSchema = z.object({
  expiresAt: isoDateTimeSchema,
  headers: z.record(z.string(), z.string()).default({}),
  partNumber: z.number().int().min(1).max(10_000),
  url: z.url({
    error: 'L’URL présignée de partie est invalide.',
  }),
}).strict();

export const prepareAudioUploadResponseSchema = z.object({
  attachmentId: z.uuid({
    error: 'L’identifiant de pièce jointe audio est invalide.',
  }),
  filename: z.string().trim().min(1),
  limits: audioUploadLimitsSchema,
  mediaType: audioAttachmentMimeSchema,
  multipart: z.object({
    partCount: z.number().int().positive(),
    partSizeBytes: z.number().int().positive(),
    uploadId: z.string().trim().min(1),
  }).strict().nullable(),
  simplePut: z.object({
    expiresAt: isoDateTimeSchema,
    headers: z.record(z.string(), z.string()).default({}),
    url: z.url({
      error: 'L’URL présignée d’upload est invalide.',
    }),
  }).strict().nullable(),
  sizeBytes: z.number().int().positive(),
  strategy: audioUploadStrategySchema,
  uploadStatus: z.literal('prepared'),
}).strict().superRefine((response, context) => {
  if (response.strategy === 'simple_put' && !response.simplePut) {
    context.addIssue({
      code: 'custom',
      message: 'Un upload simple doit exposer une URL présignée.',
      path: ['simplePut'],
    });
  }

  if (response.strategy === 'multipart' && !response.multipart) {
    context.addIssue({
      code: 'custom',
      message: 'Un upload multipart doit exposer un identifiant de session.',
      path: ['multipart'],
    });
  }
});

export const requestAudioUploadPartsSchema = z.object({
  partNumbers: z.array(z.number().int().min(1).max(10_000)).min(1).max(32),
}).strict().superRefine((payload, context) => {
  if (new Set(payload.partNumbers).size !== payload.partNumbers.length) {
    context.addIssue({
      code: 'custom',
      message: 'Chaque numéro de partie doit être unique.',
      path: ['partNumbers'],
    });
  }
});

export const requestAudioUploadPartsResponseSchema = z.object({
  attachmentId: z.uuid(),
  parts: z.array(audioPresignedPartSchema).min(1).max(32),
}).strict();

export const finalizeAudioUploadRequestSchema = z.object({
  etag: z.string().trim().min(1).max(256).optional(),
  parts: z.array(z.object({
    etag: z.string().trim().min(1).max(256),
    partNumber: z.number().int().min(1).max(10_000),
  }).strict()).min(1).max(32).optional(),
}).strict().superRefine((payload, context) => {
  if (!payload.etag && !payload.parts) {
    context.addIssue({
      code: 'custom',
      message: 'La finalisation exige un ETag simple ou la liste des parties.',
    });
  }
  if (payload.parts && new Set(payload.parts.map((part) => part.partNumber)).size !== payload.parts.length) {
    context.addIssue({
      code: 'custom',
      message: 'Chaque partie finalisée doit être unique.',
      path: ['parts'],
    });
  }
});

export const finalizeAudioUploadResponseSchema = z.object({
  attachmentId: z.uuid(),
  durationMs: z.number().int().positive().nullable(),
  errorCode: audioErrorCodeSchema.nullable(),
  filename: z.string().trim().min(1),
  mediaType: audioAttachmentMimeSchema,
  sizeBytes: z.number().int().positive(),
  uploadStatus: z.enum(['failed', 'ready']),
}).strict();

export const audioArtifactSchema = z.object({
  artifactId: z.string().trim().min(1),
  contentType: z.string().trim().min(1),
  filename: z.string().trim().min(1).max(255),
  jobId: z.uuid().nullable(),
  kind: audioArtifactKindSchema,
  sizeBytes: z.number().int().nonnegative().nullable(),
}).strict();

export const audioTranscriptionJobStatusItemSchema = z.object({
  attachmentId: z.uuid(),
  chunkCount: z.number().int().nonnegative().nullable(),
  durationMs: z.number().int().nonnegative().nullable(),
  errorCode: audioErrorCodeSchema.nullable(),
  filename: z.string().trim().min(1),
  jobId: z.uuid(),
  language: z.string().trim().min(2).max(16).nullable(),
  phase: audioTranscriptionPhaseSchema,
  preview: z.string().trim().max(AUDIO_TRANSCRIPTION_LIMITS.previewMaxChars).nullable(),
  processedChunkCount: z.number().int().nonnegative().nullable(),
  progressBasisPoints: z.number().int().min(0).max(10_000).nullable(),
  speakerCount: z.number().int().nonnegative().nullable(),
  status: audioTranscriptionJobStatusSchema,
}).strict();

export const audioTranscriptionBatchStatusResponseSchema = z.object({
  analysisErrorCode: audioErrorCodeSchema.nullable(),
  analysisProvider: audioAnalysisProviderSchema,
  artifacts: z.array(audioArtifactSchema).max(24),
  batchId: z.uuid(),
  conversationId: conversationIdSchema,
  createdAt: isoDateTimeSchema,
  failedJobs: z.number().int().nonnegative(),
  jobs: z.array(audioTranscriptionJobStatusItemSchema).max(AUDIO_TRANSCRIPTION_LIMITS.maxFilesPerMessage),
  outputPlan: audioOutputPlanSchema.nullable(),
  sourceExpired: z.boolean(),
  status: audioTranscriptionBatchStatusSchema,
  succeededJobs: z.number().int().nonnegative(),
  title: z.string().trim().min(1).max(160),
  totalDurationMs: z.number().int().nonnegative(),
  totalJobs: z.number().int().positive().max(AUDIO_TRANSCRIPTION_LIMITS.maxFilesPerMessage),
  updatedAt: isoDateTimeSchema,
}).strict().superRefine((batch, context) => {
  if (batch.succeededJobs + batch.failedJobs > batch.totalJobs) {
    context.addIssue({
      code: 'custom',
      message: 'Les compteurs de jobs dépassent le total.',
      path: ['totalJobs'],
    });
  }

  if (batch.jobs.length !== batch.totalJobs) {
    context.addIssue({
      code: 'custom',
      message: 'Le lot doit exposer exactement un statut par fichier.',
      path: ['jobs'],
    });
  }
});

export const audioTranscriptSpeakerSchema = z.object({
  displayName: z.string().trim().min(1).max(80),
  id: z.string().trim().min(1).max(64),
}).strict();

export const audioTranscriptSegmentSchema = z.object({
  endMs: z.number().int().nonnegative(),
  id: z.string().trim().min(1).max(64),
  speakerId: z.string().trim().min(1).max(64).nullable(),
  startMs: z.number().int().nonnegative(),
  text: z.string(),
  uncertain: z.boolean(),
}).strict().refine((segment) => segment.endMs >= segment.startMs, {
  error: 'La fin du segment doit être postérieure à son début.',
  path: ['endMs'],
});

export const audioCanonicalTranscriptSchema = z.object({
  language: z.string().trim().min(2).max(16).nullable(),
  models: z.object({
    aligner: z.object({
      id: z.string().trim().min(1),
      revision: z.string().trim().min(1),
    }).strict().nullable(),
    asr: z.object({
      id: z.string().trim().min(1),
      revision: z.string().trim().min(1),
    }).strict(),
    diarization: z.object({
      id: z.string().trim().min(1),
      revision: z.string().trim().min(1),
    }).strict().nullable(),
  }).strict(),
  schemaVersion: z.literal(1),
  segments: z.array(audioTranscriptSegmentSchema),
  source: z.object({
    attachmentId: z.uuid(),
    durationMs: z.number().int().nonnegative(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/u, {
      error: 'Le SHA-256 source est invalide.',
    }),
  }).strict(),
  speakerCount: z.number().int().nonnegative().nullable().optional(),
  speakers: z.array(audioTranscriptSpeakerSchema),
  warnings: z.array(z.string().trim().min(1).max(200)).max(16).optional(),
}).strict();

export const audioTranscriptResponseSchema = z.object({
  attachmentId: z.uuid(),
  batchId: z.uuid(),
  filename: z.string().trim().min(1),
  jobId: z.uuid(),
  sourceExpired: z.boolean(),
  transcript: audioCanonicalTranscriptSchema,
}).strict();

export const audioArtifactDownloadResponseSchema = z.object({
  contentType: z.string().trim().min(1),
  downloadUrl: z.url({
    error: 'L’URL de téléchargement de l’artefact est invalide.',
  }),
  expiresIn: z.number().int().positive(),
  filename: z.string().trim().min(1),
}).strict();

export const audioTranscriptionRetryResponseSchema = z.object({
  batchId: z.uuid(),
  jobId: z.uuid().nullable(),
  status: audioTranscriptionBatchStatusSchema,
}).strict();

export const audioServiceProbeResponseSchema = z.object({
  channels: z.number().int().nonnegative().nullable(),
  codec: z.string().trim().min(1).nullable(),
  container: z.string().trim().min(1).nullable(),
  durationMs: z.number().int().nonnegative(),
  hasAudio: z.boolean(),
  sampleRate: z.number().int().nonnegative().nullable(),
  sha256: z.string().regex(/^[a-f0-9]{64}$/u, {
    error: 'Le SHA-256 probe est invalide.',
  }),
  sizeBytes: z.number().int().nonnegative(),
}).strict();

export const audioServiceReadinessSchema = z.object({
  code: z.literal(AUDIO_ERROR_CODES.AUDIO_SERVICE_UNAVAILABLE).optional(),
  device: z.enum(['cpu', 'cuda', 'mps']),
  deviceAvailable: z.boolean(),
  engine: z.enum(['fake', 'qwen']),
  models: z.boolean(),
  service: z.literal('inersio-audio-service'),
  status: z.enum(['not_ready', 'ok']),
  tempDisk: z.boolean(),
  tempFreeBytes: z.number().int().nonnegative(),
}).strict();

export const audioServiceEventSchema = z.discriminatedUnion('type', [
  z.object({
    chunkCount: z.number().int().positive().nullable().optional(),
    jobId: z.uuid(),
    phase: audioTranscriptionPhaseSchema,
    processedChunkCount: z.number().int().nonnegative().nullable().optional(),
    progressBasisPoints: z.number().int().min(0).max(10_000).nullable().optional(),
    type: z.literal('progress'),
  }).strict(),
  z.object({
    checkpointObjectKeyHint: z.literal(true),
    jobId: z.uuid(),
    processedChunkCount: z.number().int().nonnegative(),
    type: z.literal('checkpoint'),
  }).strict(),
  z.object({
    durationMs: z.number().int().nonnegative(),
    jobId: z.uuid(),
    language: z.string().trim().min(2).max(16).nullable(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/u),
    speakerCount: z.number().int().nonnegative().nullable(),
    type: z.literal('completed'),
  }).strict(),
  z.object({
    errorCode: audioErrorCodeSchema,
    jobId: z.uuid(),
    retryable: z.boolean(),
    type: z.literal('failed'),
  }).strict(),
  z.object({
    jobId: z.uuid(),
    type: z.literal('canceled'),
  }).strict(),
]);

export const chatAudioTranscriptionBatchDataSchema = z.object({
  batchId: z.uuid({
    error: 'L’identifiant du lot de transcription est invalide.',
  }),
  title: z.string().trim().min(1, {
    error: 'Le titre du lot de transcription est requis.',
  }).max(160, {
    error: 'Le titre du lot de transcription est trop long.',
  }),
}).strict();

export type AudioAttachmentMime = z.infer<typeof audioAttachmentMimeSchema>;
export type AudioAnalysisProviderDTO = z.infer<typeof audioAnalysisProviderSchema>;
export type ChatAttachmentKind = z.infer<typeof chatAttachmentKindSchema>;
export type AudioUploadStatus = z.infer<typeof audioUploadStatusSchema>;
export type AudioUploadStrategy = z.infer<typeof audioUploadStrategySchema>;
export type AudioTranscriptionJobStatus = z.infer<typeof audioTranscriptionJobStatusSchema>;
export type AudioTranscriptionPhase = z.infer<typeof audioTranscriptionPhaseSchema>;
export type AudioTranscriptionBatchStatus = z.infer<typeof audioTranscriptionBatchStatusSchema>;
export type AudioOutputPlanDTO = z.infer<typeof audioOutputPlanSchema>;
export type PrepareAudioUploadRequestDTO = z.infer<typeof prepareAudioUploadRequestSchema>;
export type PrepareAudioUploadResponseDTO = z.infer<typeof prepareAudioUploadResponseSchema>;
export type RequestAudioUploadPartsDTO = z.infer<typeof requestAudioUploadPartsSchema>;
export type RequestAudioUploadPartsResponseDTO = z.infer<typeof requestAudioUploadPartsResponseSchema>;
export type FinalizeAudioUploadRequestDTO = z.infer<typeof finalizeAudioUploadRequestSchema>;
export type FinalizeAudioUploadResponseDTO = z.infer<typeof finalizeAudioUploadResponseSchema>;
export type AudioArtifactDTO = z.infer<typeof audioArtifactSchema>;
export type AudioTranscriptionJobStatusItemDTO = z.infer<typeof audioTranscriptionJobStatusItemSchema>;
export type AudioTranscriptionBatchStatusResponseDTO = z.infer<typeof audioTranscriptionBatchStatusResponseSchema>;
export type AudioCanonicalTranscriptDTO = z.infer<typeof audioCanonicalTranscriptSchema>;
export type AudioTranscriptResponseDTO = z.infer<typeof audioTranscriptResponseSchema>;
export type AudioArtifactDownloadResponseDTO = z.infer<typeof audioArtifactDownloadResponseSchema>;
export type AudioTranscriptionRetryResponseDTO = z.infer<typeof audioTranscriptionRetryResponseSchema>;
export type AudioServiceProbeResponseDTO = z.infer<typeof audioServiceProbeResponseSchema>;
export type AudioServiceReadinessDTO = z.infer<typeof audioServiceReadinessSchema>;
export type AudioServiceEventDTO = z.infer<typeof audioServiceEventSchema>;
export type ChatAudioTranscriptionBatchDataDTO = z.infer<typeof chatAudioTranscriptionBatchDataSchema>;
